import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { WishlistItemSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type ItemDraft = {
  label: string;
  amountCoins: string;
  note: string;
};

const emptyDraft = (): ItemDraft => ({
  label: '',
  amountCoins: '100',
  note: '',
});

export function ModelWishlistCard(): ReactElement {
  const { api } = useAuth();
  const [items, setItems] = useState<WishlistItemSummary[]>([]);
  const [draft, setDraft] = useState<ItemDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyWishlist();
    setItems(result.items);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.wishlist.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    const amount = Number.parseInt(draft.amountCoins, 10);
    if (!draft.label.trim() || !Number.isFinite(amount) || amount < 1) {
      setError(t('common.studio.wishlist.invalidItem'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createWishlistItem({
        label: draft.label.trim(),
        amountCoins: amount,
        note: draft.note.trim() || null,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.wishlist.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(item: WishlistItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateWishlistItem(item.id, { enabled: !item.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.wishlist.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleFulfilled(item: WishlistItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateWishlistItem(item.id, { fulfilled: !item.fulfilled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.wishlist.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(itemId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteWishlistItem(itemId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.wishlist.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.wishlist.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.wishlist.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.wishlist.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {items.map((item) => (
          <li
            key={item.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: item.enabled && !item.fulfilled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {item.label} · {item.amountCoins}
              {item.note ? ` · ${item.note}` : ''}
              {item.fulfilled ? ` (${t('common.studio.wishlist.fulfilled')})` : ''}
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={item.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(item);
                }}
              />
              {t('common.studio.wishlist.enabled')}
            </label>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={item.fulfilled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleFulfilled(item);
                }}
              />
              {t('common.studio.wishlist.fulfilled')}
            </label>
            <Button
              label={t('common.studio.wishlist.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(item.id);
              }}
            />
          </li>
        ))}
      </ul>

      {items.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.wishlist.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.wishlist.label')}
          <input
            className="fm-input"
            value={draft.label}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, label: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.wishlist.amount')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.amountCoins}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, amountCoins: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.wishlist.note')}
          <input
            className="fm-input"
            value={draft.note}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, note: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.wishlist.saving') : t('common.studio.wishlist.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
